'use client';

import { useState } from 'react';
import { BellPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/use-session';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { FeedAlertDialog } from './FeedAlertDialog';

/**
 * "Alert für diese Filter" — the entry point a filterable page puts next to
 * its results. Guests get a sign-in button that returns to the same filtered
 * view; an unfiltered alert first asks for confirmation, because it covers
 * every single event of that kind.
 */
export function FeedAlertTrigger({
  feedType,
  filters,
  isUnfiltered,
  unfilteredDescription,
  onAdjustFilters,
  label,
  signInLabel,
  className,
  onSignIn,
}: {
  feedType: string;
  filters: Record<string, unknown>;
  /** No narrowing filter is set — the alert would cover everything. */
  isUnfiltered: boolean;
  unfilteredDescription: string;
  /** Opens the page's filter panel from the confirmation. */
  onAdjustFilters?: () => void;
  label?: string;
  signInLabel?: string;
  className?: string;
  /** Overrides the default login redirect for guests. */
  onSignIn?: () => void;
}) {
  const { t } = useTranslation();
  const { data: session, status } = useSession();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const buttonClass = cn(
    'h-auto min-h-11 whitespace-normal rounded-lg lg:min-h-9',
    className,
  );

  if (status === 'loading') return null;

  if (!session) {
    return (
      <Button
        className={buttonClass}
        onClick={() => {
          if (onSignIn) return onSignIn();
          const returnTo = window.location.pathname + window.location.search;
          window.location.href = `/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
        }}
      >
        <BellPlus className="size-4" />
        {signInLabel ?? t('alerts.trigger.signIn')}
      </Button>
    );
  }

  return (
    <>
      <Button
        className={buttonClass}
        onClick={() => (isUnfiltered ? setConfirmOpen(true) : setDialogOpen(true))}
      >
        <BellPlus className="size-4" />
        {label ?? t('alerts.trigger.label')}
      </Button>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('alerts.trigger.unfilteredTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{unfilteredDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {onAdjustFilters ? (
              <AlertDialogCancel onClick={onAdjustFilters}>
                {t('alerts.trigger.adjustFilters')}
              </AlertDialogCancel>
            ) : (
              <AlertDialogCancel>{t('common.buttons.cancel', 'Cancel')}</AlertDialogCancel>
            )}
            <AlertDialogAction onClick={() => setDialogOpen(true)}>
              {t('alerts.trigger.unfilteredConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <FeedAlertDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        feedType={feedType}
        filters={filters}
        // The confirmation already asked; the dialog only states the scope.
        warning={isUnfiltered ? t('alerts.trigger.unfilteredStatement') : null}
      />
    </>
  );
}
